import { FunctionComponent, useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import capitalize from 'lodash/capitalize';
import { ReferrerQueryString } from '@fff-web/fff-analytics';
import { usePriceLockEligibility, useUserContext } from '@fff-web/fff-utilities';
import { type Subscription } from '@fff-web/growth-sdk';
import { LOCK_IN_RATE_PATH } from '../../../../utils/routePaths';
import {
  BOX_STATUS,
  CANCEL_BOX_SUBSCRIPTION,
  CANCEL_CLUB_MEMBERSHIP,
  CANCEL_SUBSCRIPTION_LABEL,
  getCancelBoxSubscriptionLabel,
  Plans,
  REACTIVATE_MEMBERSHIP_LABEL,
  SUBSCRIPTION_STATUS,
} from '../../../constants';
import { ClubMembershipModal } from '../../ClubMembershipModal';
import { SubscriptionStatus } from '../../MyAccountPage';
import { getBoxStatus } from '../../services/api/box';
import { CANCEL_FLOW_EVENT_NAME } from '../../utils/segment';
import ManageMembershipLink from './ManageMembershipLink';
import {
  trackClickCancelSubscription,
  trackClickReactivateMembership,
  trackClickSwapForCredit,
} from './trackEvents';
import * as manageMembershipStyles from './ManageMembership.module.css';

const REFERRER_QUERY_STRING = `?referrer=${ReferrerQueryString.myAccount}`;
const CANCEL_SUBSCRIPTION_AND_BOX_COPY =
  'Cancellation takes effect at the next billing cycle and does not impact pre-paid boxes.';
const CANCEL_SUBSCRIPTION_COPY =
  'By ending your account you will lose access to your FabFitFun benefits.';

type ManageMembershipProps = {
  isClubMember: boolean;
  clubMemberSubscriptionId?: string | null;
  subscriptionState: SubscriptionStatus;
  refetchClubMemberSubscription: () => void;
  boxSubscriptions?: Subscription[] | null;
};

const ManageMembership: FunctionComponent<ManageMembershipProps> = ({
  isClubMember,
  clubMemberSubscriptionId,
  subscriptionState,
  refetchClubMemberSubscription,
  boxSubscriptions,
}) => {
  const [isCancelMembershipModalOpen, setIsCancelMembershipModalOpen] = useState(false);
  const [cancelBoxModalStates, setCancelBoxModalStates] = useState<Record<string, boolean>>({});
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  const [boxStatus, setBoxStatus] = useState('');

  const { priceLock: priceLockFF } = useFlags();
  const { userInfo } = useUserContext();
  const { isEligible: isPriceLockEligible } = usePriceLockEligibility({
    priceLockFF,
    enabled: !isClubMember,
  });
  const { user } = userInfo;
  const { planCode } = user;

  const displaySwapForCredit =
    planCode === Plans.TRIAL
      ? subscriptionState !== SUBSCRIPTION_STATUS.ACTIVE
      : boxStatus !== BOX_STATUS.SKIPPED_INTRO;

  const isCanceled = subscriptionState === SUBSCRIPTION_STATUS.CANCELED;
  const isExpired = subscriptionState === SUBSCRIPTION_STATUS.EXPIRED;
  const cancelSubscriptionCopy = boxSubscriptions && boxSubscriptions.length > 0
    ? `Cancel ${capitalize(user.subscriptionType)} Box Subscription`
    : CANCEL_SUBSCRIPTION_LABEL;
  const cancelSubscriptionSubcopy = boxSubscriptions && boxSubscriptions.length > 0
    ? CANCEL_SUBSCRIPTION_AND_BOX_COPY
    : CANCEL_SUBSCRIPTION_COPY;

  const clickSwapForCredit = () => {
    trackClickSwapForCredit(planCode);
  };

  useEffect(() => {
    if (isClubMember) return;

    const fetchBoxStatus = async () => {
      const { boxState } = await getBoxStatus();
      setBoxStatus(boxState);
    };

    fetchBoxStatus();
  }, [isClubMember]);

  const handleClickCancelClubMembershipCTA = () => {
    trackClickCancelSubscription(
      CANCEL_FLOW_EVENT_NAME.started,
      CANCEL_CLUB_MEMBERSHIP.label,
      CANCEL_CLUB_MEMBERSHIP.type,
    );
    setIsCancelMembershipModalOpen(true);
  };

  const handleClickCancelBoxSubscriptionCTA = (subscriptionId: string, subscriptionName: string) => {
    trackClickCancelSubscription(
      CANCEL_FLOW_EVENT_NAME.started,
      `Cancel ${subscriptionName} Subscription`,
      CANCEL_BOX_SUBSCRIPTION.type,
    );
    setCancelBoxModalStates(prev => ({ ...prev, [subscriptionId]: true }));
  };

  const getLinks = () => {
    if (isClubMember) {
      if (isCanceled || isExpired) {
        return (
          <ManageMembershipLink
            onClickCTA={() => setIsReactivateModalOpen(true)}
            title="Reactivate Membership"
            subtitle="Reactivate today and gain instant access to all of the FabFitFun perks."
            isButton
          />
        );
      }
      return (
        <>
          {boxSubscriptions && boxSubscriptions.length > 0 ? (
            boxSubscriptions.map((boxSub) => (
              <ManageMembershipLink
                key={boxSub.id}
                onClickCTA={() => handleClickCancelBoxSubscriptionCTA(boxSub.id, boxSub.name)}
                title={getCancelBoxSubscriptionLabel(boxSub)}
                subtitle={CANCEL_SUBSCRIPTION_AND_BOX_COPY}
                isButton
              />
            ))
          ) : (
            <ManageMembershipLink
              onClickCTA={handleClickCancelClubMembershipCTA}
              title={CANCEL_CLUB_MEMBERSHIP.label}
              subtitle="By canceling your membership you will lose access to your FabFitFun benefits."
              isButton
            />
          )}
        </>
      );
    }

    if (isExpired) {
      return (
        <ManageMembershipLink
          path={`/reactivate-my-membership${REFERRER_QUERY_STRING}`}
          onClickCTA={trackClickReactivateMembership}
          title={REACTIVATE_MEMBERSHIP_LABEL}
          subtitle="Reactivate to a seasonal or annual membership today and gain
          instant access to all of the FabFitFun perks."
        />
      );
    }

    return (
      <>
        {isPriceLockEligible && (
          <ManageMembershipLink
            path={LOCK_IN_RATE_PATH}
            title="Lock in Your Rate"
            subtitle="Limited time offer. Lock in your current
              price before the membership price increases."
          />
        )}
        {displaySwapForCredit && (
          <ManageMembershipLink
            path={`/swap-for-credit${REFERRER_QUERY_STRING}`}
            onClickCTA={clickSwapForCredit}
            title="Swap for credit"
            subtitle="Swap your
              box for e-commerce credit and create your own shopping experience – so you
              decide how to shop with us!"
          />
        )}
        {!isCanceled ? (
          <ManageMembershipLink
            path={`/cancel-subscription${REFERRER_QUERY_STRING}`}
            title={cancelSubscriptionCopy}
            subtitle={cancelSubscriptionSubcopy}
          />
        ) : (
          <ManageMembershipLink
            path={`/reactivate-today${REFERRER_QUERY_STRING}`}
            title={REACTIVATE_MEMBERSHIP_LABEL}
            subtitle="Reactivate to a seasonal or annual membership today and gain
                instant access to all of the FabFitFun perks."
          />
        )}
        {boxSubscriptions && boxSubscriptions.length > 0 && (
          boxSubscriptions.map((boxSub) => (
            <ManageMembershipLink
              key={boxSub.id}
              onClickCTA={() => handleClickCancelBoxSubscriptionCTA(boxSub.id, boxSub.name)}
              title={getCancelBoxSubscriptionLabel(boxSub)}
              subtitle={CANCEL_SUBSCRIPTION_AND_BOX_COPY}
              isButton
            />
          ))
        )}
      </>
    );
  };

  const onSubscriptionChangeDone = () => {
    refetchClubMemberSubscription();
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const onClubMembershipChangeDone = () => {
    trackClickCancelSubscription(
      CANCEL_FLOW_EVENT_NAME.completed,
      CANCEL_CLUB_MEMBERSHIP.label,
      CANCEL_CLUB_MEMBERSHIP.type,
    );
    onSubscriptionChangeDone();
  };

  const onBoxSubscriptionChangeDone = () => {
    trackClickCancelSubscription(
      CANCEL_FLOW_EVENT_NAME.completed,
      CANCEL_BOX_SUBSCRIPTION.label,
      CANCEL_BOX_SUBSCRIPTION.type,
    );
    onSubscriptionChangeDone();
  };

  return (
    <div className={`${manageMembershipStyles.container}`}>
      {getLinks()}
      {clubMemberSubscriptionId && (
        <ClubMembershipModal
          isOpen={isCancelMembershipModalOpen}
          subscriptionId={clubMemberSubscriptionId}
          onClose={() => setIsCancelMembershipModalOpen(false)}
          variant="cancel"
          onDone={onClubMembershipChangeDone}
        />
      )}
      {boxSubscriptions && boxSubscriptions.map((boxSub) => (
        <ClubMembershipModal
          key={boxSub.id}
          isOpen={cancelBoxModalStates[boxSub.id] || false}
          subscriptionId={boxSub.id}
          onClose={() => setCancelBoxModalStates(prev => ({ ...prev, [boxSub.id]: false }))}
          variant="cancel"
          onDone={onBoxSubscriptionChangeDone}
          isBoxSubscription
          boxSubscriptionName={boxSub.name}
        />
      ))}
      {clubMemberSubscriptionId && (
        <ClubMembershipModal
          isOpen={isReactivateModalOpen}
          subscriptionId={clubMemberSubscriptionId}
          onClose={() => setIsReactivateModalOpen(false)}
          variant="reactivate"
          onDone={onSubscriptionChangeDone}
        />
      )}
    </div>
  );
};

export default ManageMembership;
